<script lang="ts" module>
  import {
    type CivilDate,
    GREGORIAN_MONTH_NAMES,
    JALALI_MONTH_NAMES,
    JALALI_WEEKDAY_NAMES,
    addGregorianMonths,
    addJalaliMonths,
    formatGregorianLongDate,
    formatGregorianMonthYear,
    formatJalaliLongDate,
    formatJalaliMonthYear,
    gregorianMonthDays,
    gregorianMonthOf,
    gregorianToJalali,
    jalaliMonthDays,
    jalaliMonthOf,
    toPersianDigits,
  } from '@clinic/money-date';

  /** `۸ مهر ۱۴۰۵`, optionally with the weekday in front. */
  export function jalaliDateLabel(date: CivilDate, weekday = false): string {
    if (weekday) return formatJalaliLongDate(date);
    const j = gregorianToJalali(date.year, date.month, date.day);
    return `${toPersianDigits(j.jd)} ${JALALI_MONTH_NAMES[j.jm - 1] as string} ${toPersianDigits(j.jy)}`;
  }

  /** The top layer keeps the calendar above panels and dialogs; without it (tests) it renders in place. */
  const HAS_POPOVER = typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';
  const WEEKDAY_INITIALS = JALALI_WEEKDAY_NAMES.map((name) => name.slice(0, 1));

  /** A month in the picker's own calendar. */
  interface Month {
    year: number;
    month: number;
  }

  /** What the picker needs from a calendar; both lay out Saturday-first weeks like the rest of the app. */
  interface Calendar {
    monthNames: readonly string[];
    monthOf(date: CivilDate): Month;
    addMonths(month: Month, delta: number): Month;
    monthDays(month: Month): { offset: number; days: CivilDate[] };
    dayOfMonth(date: CivilDate): number;
    /** With the weekday: a day's accessible name. */
    longLabel(date: CivilDate): string;
    shortLabel(date: CivilDate): string;
    monthYear(month: Month): string;
  }

  const jalali = (m: Month) => ({ jy: m.year, jm: m.month });
  const JALALI: Calendar = {
    monthNames: JALALI_MONTH_NAMES,
    monthOf: (date) => {
      const j = jalaliMonthOf(date);
      return { year: j.jy, month: j.jm };
    },
    addMonths: (month, delta) => {
      const j = addJalaliMonths(jalali(month), delta);
      return { year: j.jy, month: j.jm };
    },
    monthDays: (month) => jalaliMonthDays(jalali(month)),
    dayOfMonth: (date) => gregorianToJalali(date.year, date.month, date.day).jd,
    longLabel: formatJalaliLongDate,
    shortLabel: (date) => jalaliDateLabel(date),
    monthYear: (month) => formatJalaliMonthYear(jalali(month)),
  };

  /** For dates printed Gregorian, like an expiry on a box (BR-04). */
  const GREGORIAN: Calendar = {
    monthNames: GREGORIAN_MONTH_NAMES,
    monthOf: gregorianMonthOf,
    addMonths: addGregorianMonths,
    monthDays: gregorianMonthDays,
    dayOfMonth: (date) => date.day,
    longLabel: (date) => formatGregorianLongDate(date),
    shortLabel: (date) => formatGregorianLongDate(date, false),
    monthYear: formatGregorianMonthYear,
  };
</script>

<script lang="ts">
  import { addDays, civilDateOf, parseIsoDate, toIsoDate } from '@clinic/money-date';
  import { CalendarDays, ChevronLeft, ChevronRight } from '@lucide/svelte';
  import { tick, type Snippet } from 'svelte';
  import { fa } from '../i18n/fa';
  import { cn } from '../lib/cn';

  /**
   * A Jalali (Solar Hijri) date picker that opens as a calendar. The value stays a Gregorian ISO date,
   * like everywhere else in the app (BR-03): Jalali is presentation only. Arrow keys move by day and
   * week, PageUp/PageDown by month; Escape or a click outside closes it. `calendar="gregorian"` shows
   * Gregorian months instead, for dates printed that way (an expiry, BR-04).
   */
  let {
    value,
    calendar = 'jalali',
    onChange,
    id,
    min,
    max,
    clearable,
    weekday,
    counts,
    onView,
    disabled,
    class: className,
    children,
    'aria-label': ariaLabel,
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedby,
  }: {
    /** `YYYY-MM-DD` (Gregorian, as the API stores it), or null for no date. */
    value: string | null;
    onChange: (value: string | null) => void;
    calendar?: 'jalali' | 'gregorian';
    id?: string;
    /** Inclusive bounds, `YYYY-MM-DD`. Days outside them cannot be chosen. */
    min?: string;
    max?: string;
    /** Offers «پاک کردن» for optional dates. */
    clearable?: boolean;
    /** Shows the weekday in the chosen date's label. */
    weekday?: boolean;
    /** A number on each day (`YYYY-MM-DD` to count), e.g. the patients booked; 0 or missing shows none. */
    counts?: Readonly<Record<string, number>>;
    /** Told the first and last day of the month on screen while the calendar is open, to load `counts`. */
    onView?: (from: string, to: string) => void;
    disabled?: boolean;
    /** Replaces the form-field look of the trigger (e.g. a page title that opens the calendar). */
    class?: string;
    /** Replaces the trigger's content; the accessible name then comes from `aria-label`. */
    children?: Snippet;
    'aria-label'?: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
  } = $props();

  const panelId = $props.id();
  let trigger = $state<HTMLButtonElement>();
  let panel = $state<HTMLDivElement>();
  let focusDay = false;
  const today = civilDateOf(new Date());
  const selected = $derived(value ? parseIsoDate(value) : null);
  const cal = $derived(calendar === 'gregorian' ? GREGORIAN : JALALI);
  let open = $state(false);
  let cursor = $state<CivilDate>(today);
  // svelte-ignore state_referenced_locally
  let view = $state<Month>(cal.monthOf(today));

  function clamp(date: CivilDate): CivilDate {
    const iso = toIsoDate(date);
    if (min && iso < min) return parseIsoDate(min) ?? date;
    if (max && iso > max) return parseIsoDate(max) ?? date;
    return date;
  }

  async function focusCursor(): Promise<void> {
    await tick();
    if (!open || !focusDay) return;
    focusDay = false;
    panel?.querySelector<HTMLButtonElement>(`[data-date="${toIsoDate(cursor)}"]`)?.focus();
  }

  function show(): void {
    const start = clamp(selected ?? today);
    cursor = start;
    view = cal.monthOf(start);
    focusDay = true;
    open = true;
    void focusCursor();
  }
  const hide = () => {
    open = false;
  };

  // `open` decides; the popover follows it and is placed under its trigger (above when there is
  // no room), inside the viewport. A light dismiss (Escape, click outside) reports back in ontoggle.
  $effect.pre(() => {
    const el = panel;
    void view.year;
    void view.month;
    if (!el || !HAS_POPOVER) return;
    const shown = el.matches(':popover-open');
    if (!open) {
      if (shown) el.hidePopover();
      return;
    }
    if (!shown) el.showPopover();
    const place = () => {
      const t = trigger?.getBoundingClientRect();
      if (!t) return;
      const p = el.getBoundingClientRect();
      const gap = 6;
      const edge = 8;
      const below = t.bottom + gap;
      const top = below + p.height > window.innerHeight - edge ? Math.max(edge, t.top - gap - p.height) : below;
      // RTL: line up the right edges, then keep it on screen.
      const left = Math.min(Math.max(edge, t.right - p.width), window.innerWidth - p.width - edge);
      el.style.top = `${top}px`;
      el.style.left = `${Math.max(edge, left)}px`;
    };
    place();
    // This runs before the month grid is in the DOM: measure again once it is, so a calendar that
    // does not fit below its trigger opens above it instead of past the bottom of the screen.
    void tick().then(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  });

  const inRange = (date: CivilDate) => {
    const iso = toIsoDate(date);
    return (!min || iso >= min) && (!max || iso <= max);
  };
  function moveTo(date: CivilDate): void {
    const next = clamp(date);
    cursor = next;
    view = cal.monthOf(next);
    focusDay = true;
    void focusCursor();
  }
  function pick(date: CivilDate | null): void {
    onChange(date ? toIsoDate(date) : null);
    hide();
    trigger?.focus();
  }
  function onGridKey(e: KeyboardEvent): void {
    // The page is RTL: the next day is to the left.
    const days = ({ ArrowLeft: 1, ArrowRight: -1, ArrowUp: -7, ArrowDown: 7 } as Record<string, number>)[e.key];
    if (days !== undefined) {
      e.preventDefault();
      moveTo(addDays(cursor, days));
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      const month = cal.addMonths(cal.monthOf(cursor), e.key === 'PageUp' ? -1 : 1);
      const day = cal.dayOfMonth(cursor);
      const list = cal.monthDays(month).days;
      moveTo(list[Math.min(day, list.length) - 1] as CivilDate);
    }
  }

  const grid = $derived(cal.monthDays(view));
  $effect(() => {
    const days = grid.days;
    if (!open || !onView || days.length === 0) return;
    onView(toIsoDate(days[0] as CivilDate), toIsoDate(days[days.length - 1] as CivilDate));
  });
  const todayYear = $derived(cal.monthOf(today).year);
  const firstYear = $derived(Math.min(view.year, min ? cal.monthOf(parseIsoDate(min) ?? today).year : todayYear - 5));
  const lastYear = $derived(Math.max(view.year, max ? cal.monthOf(parseIsoDate(max) ?? today).year : todayYear + 5));
  const years = $derived(Array.from({ length: lastYear - firstYear + 1 }, (_, i) => firstYear + i));
  const selectedIso = $derived(selected ? toIsoDate(selected) : null);
  const cursorMonth = $derived(cal.monthOf(cursor));
  const cursorInView = $derived(cursorMonth.year === view.year && cursorMonth.month === view.month);
  const valueId = `${panelId}-value`;
  // Inside a <Field> the name is "label, chosen date"; the label alone would hide the value.
  const labelledBy = $derived(id && !ariaLabel ? `${id}-label ${valueId}` : undefined);
  const todayIso = toIsoDate(today);
  const cursorIso = $derived(toIsoDate(cursor));
  const selectClass =
    'min-h-9 rounded-md border border-border-strong bg-surface px-1.5 text-sm text-text focus:border-accent focus:outline-none';
</script>

<span class={cn('relative', !className && 'block')}>
  <!-- The trigger stands in for a field, so it carries the field's invalid state (as on the React site). -->
  <!-- svelte-ignore a11y_role_supports_aria_props_implicit -->
  <button
    bind:this={trigger}
    type="button"
    {id}
    {disabled}
    aria-haspopup="dialog"
    aria-expanded={open}
    aria-controls={panelId}
    aria-labelledby={labelledBy}
    onclick={() => (open ? hide() : show())}
    class={className ??
      cn(
        'flex min-h-11 w-full items-center justify-between gap-2 rounded-md border border-border-strong bg-surface px-3 text-start text-base',
        'focus:border-accent focus:outline-none aria-invalid:border-danger disabled:opacity-55',
        selected ? 'text-text' : 'text-text-4',
      )}
    aria-label={ariaLabel}
    aria-invalid={ariaInvalid}
    aria-describedby={ariaDescribedby}
  >
    {#if children}
      {@render children()}
    {:else}
      <span id={valueId} class="truncate">{selected ? (weekday ? cal.longLabel(selected) : cal.shortLabel(selected)) : fa.datePicker.placeholder}</span><CalendarDays
        class="size-4 shrink-0 text-text-3"
        aria-hidden="true"
      />
    {/if}
  </button>

  <div
    bind:this={panel}
    id={panelId}
    role="dialog"
    aria-label={calendar === 'gregorian' ? fa.datePicker.gregorianTitle : fa.datePicker.title}
    popover={HAS_POPOVER ? 'auto' : undefined}
    ontoggle={(e) => {
      if ((e as ToggleEvent).newState === 'closed') open = false;
    }}
    hidden={!HAS_POPOVER && !open}
    class={cn(
      'pop-enter w-[min(19rem,calc(100vw-1rem))] rounded-lg border border-[var(--neu-edge)] bg-surface p-3 text-text shadow-raised-lg',
      HAS_POPOVER ? 'fixed inset-auto m-0' : 'absolute z-10 mt-1',
    )}
  >
    {#if open}
      <div class="mb-2 flex items-center gap-1">
        <button
          type="button"
          onclick={() => (view = cal.addMonths(view, -1))}
          class="flex size-9 shrink-0 items-center justify-center rounded-md text-text-2 hover:bg-surface-3"
          aria-label={fa.datePicker.prevMonth}
        >
          <ChevronRight class="size-5" aria-hidden="true" />
        </button>
        <select
          value={view.month}
          onchange={(e) => (view = { year: view.year, month: Number(e.currentTarget.value) })}
          aria-label={fa.datePicker.month}
          class={cn(selectClass, 'min-w-0 flex-1')}
        >
          {#each cal.monthNames as name, i (name)}<option value={i + 1}>{name}</option>{/each}
        </select>
        <select
          value={view.year}
          onchange={(e) => (view = { year: Number(e.currentTarget.value), month: view.month })}
          aria-label={fa.datePicker.year}
          class={selectClass}
        >
          {#each years as y (y)}<option value={y}>{toPersianDigits(y)}</option>{/each}
        </select>
        <button
          type="button"
          onclick={() => (view = cal.addMonths(view, 1))}
          class="flex size-9 shrink-0 items-center justify-center rounded-md text-text-2 hover:bg-surface-3"
          aria-label={fa.datePicker.nextMonth}
        >
          <ChevronLeft class="size-5" aria-hidden="true" />
        </button>
      </div>

      <!-- The days inside are the focusable cells; the grid only routes their arrow keys. -->
      <!-- svelte-ignore a11y_interactive_supports_focus -->
      <div role="grid" aria-label={cal.monthYear(view)} onkeydown={onGridKey}>
        <div role="row" class="grid grid-cols-7 text-center text-xs text-text-3">
          {#each WEEKDAY_INITIALS as initial, i (JALALI_WEEKDAY_NAMES[i])}
            <span role="columnheader" aria-label={JALALI_WEEKDAY_NAMES[i]} class="py-1">{initial}</span>
          {/each}
        </div>
        <div class="grid grid-cols-7 gap-y-0.5">
          {#each { length: grid.offset }, i (i)}<span aria-hidden="true"></span>{/each}
          {#each grid.days as date, i (toIsoDate(date))}
            {@const iso = toIsoDate(date)}
            {@const isSelected = iso === selectedIso}
            {@const count = counts?.[iso] ?? 0}
            <button
              type="button"
              data-date={iso}
              disabled={!inRange(date)}
              tabindex={iso === cursorIso || (i === 0 && !cursorInView) ? 0 : -1}
              aria-label={count > 0 ? `${cal.longLabel(date)}، ${fa.datePicker.count(count)}` : cal.longLabel(date)}
              aria-pressed={isSelected}
              aria-current={iso === todayIso ? 'date' : undefined}
              onclick={() => pick(date)}
              onfocus={() => (cursor = date)}
              class={cn(
                'relative mx-auto flex aspect-square w-full max-w-10 items-center justify-center rounded-full text-sm tabular-nums',
                'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--accent)] disabled:text-text-4 disabled:opacity-50',
                isSelected
                  ? 'bg-accent-solid font-bold text-on-accent'
                  : iso === todayIso
                    ? 'border border-accent-line font-bold text-accent hover:bg-accent-soft'
                    : 'hover:bg-surface-3',
              )}
              >{toPersianDigits(i + 1)}{#if count > 0}<span
                  aria-hidden="true"
                  class="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border border-info-line bg-info-soft px-0.5 text-[0.625rem] leading-none font-bold text-info"
                  >{toPersianDigits(count)}</span
                >{/if}</button
            >
          {/each}
        </div>
      </div>

      <div class="mt-2 flex items-center justify-between border-t border-hairline pt-2">
        <button
          type="button"
          disabled={!inRange(today)}
          onclick={() => pick(today)}
          class="min-h-9 rounded-md px-2 text-sm text-accent hover:bg-accent-soft disabled:opacity-50">{fa.datePicker.today}</button
        >
        {#if clearable && selected}
          <button type="button" onclick={() => pick(null)} class="min-h-9 rounded-md px-2 text-sm text-text-2 hover:bg-surface-3"
            >{fa.datePicker.clear}</button
          >
        {/if}
      </div>
    {/if}
  </div>
</span>
